import React, { useMemo, useState } from 'react';
import {
  codeBoxClass,
  detailCardClass,
  inputClass,
  primaryButtonClass,
  secondaryButtonClass,
  selectClass,
  sectionTitleClass,
} from './ui';

const SCOPE_OPTIONS = [
  'activities:read', 'activities:write', 'activities:delete',
  'players:read', 'players:write', 'players:delete',
  'sports:read', 'sports:write', 'sports:delete',
  'measurements:read', 'profile:read', 'sessions:read',
];

const READ_ONLY_FIELDS = new Set(['applicationId', 'partnerId']);
const HIDDEN_FIELDS = new Set(['createdAt', 'updatedAt', 'deletedAt']);

const OPTIONAL_STRING_FIELDS = new Set(['logoURL', 'redirectURL', 'webHookURL']);

const formatLabel = (key) =>
  key.replace(/([A-Z])/g, ' $1').replace(/^./, (char) => char.toUpperCase());

const normalizeForCompare = (value) => {
  if (Array.isArray(value)) return [...value].sort();
  if (value === undefined) return null;
  return value;
};

const areEqual = (a, b) => {
  const normalizedA = normalizeForCompare(a);
  const normalizedB = normalizeForCompare(b);

  if (Array.isArray(normalizedA) && Array.isArray(normalizedB)) {
    if (normalizedA.length !== normalizedB.length) return false;
    return normalizedA.every((value, index) => value === normalizedB[index]);
  }

  return normalizedA === normalizedB;
};

const toStringValue = (value) => {
  if (value === null || value === undefined) return '';
  return String(value);
};

const PartnerApplicationEditor = ({
  application,
  saving,
  error,
  onSave,
  onCancel,
}) => {
  const [form, setForm] = useState(application || {});
  const editableKeys = useMemo(
    () => Object.keys(application || {}).filter((key) => !HIDDEN_FIELDS.has(key)),
    [application]
  );

  const handleTextChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleBooleanChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value === 'true' }));
  };

  const handleScopesChange = (event) => {
    const selected = Array.from(event.target.selectedOptions).map((option) => option.value);
    setForm((prev) => ({ ...prev, scopes: selected }));
  };

  const buildChangedBody = () => {
    const body = {};

    editableKeys.forEach((key) => {
      if (READ_ONLY_FIELDS.has(key)) return;

      const originalValue = application[key];
      const newValue = form[key];

      if (!areEqual(originalValue, newValue)) {
        if (OPTIONAL_STRING_FIELDS.has(key) && typeof newValue === 'string' && newValue.trim() === '') {
          return;
        }

        body[key] = typeof newValue === 'string' ? newValue.trim() : newValue;
      }
    });

    return body;
  };

  return (
    <div className={detailCardClass}>
      <div className="mb-4">
        <h4 className={sectionTitleClass}>Update Application</h4>
      </div>

      {error && (
        <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {editableKeys.map((key) => {
          const value = form[key];
          const readOnly = READ_ONLY_FIELDS.has(key);

          if (key === 'scopes') {
            return (
              <div key={key} className="md:col-span-2">
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-600">
                  Scopes <span className="text-rose-500">*</span>
                </label>
                <select
                  multiple
                  value={Array.isArray(value) ? value : []}
                  onChange={handleScopesChange}
                  className={`${selectClass} h-40`}
                  disabled={saving}
                >
                  {SCOPE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            );
          }

          if (typeof value === 'boolean') {
            return (
              <div key={key}>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-600">
                  {formatLabel(key)}
                </label>
                <select
                  value={value ? 'true' : 'false'}
                  onChange={(event) => handleBooleanChange(key, event.target.value)}
                  disabled={saving}
                  className={selectClass}
                >
                  <option value="true">true</option>
                  <option value="false">false</option>
                </select>
              </div>
            );
          }

          return (
            <div key={key}>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-600">
                {formatLabel(key)}
                {(key === 'name' || key === 'description') && <span className="text-rose-500"> *</span>}
              </label>
              {readOnly ? (
                <div className={codeBoxClass}>{toStringValue(value)}</div>
              ) : (
                <input
                  type="text"
                  value={toStringValue(value)}
                  onChange={(event) => handleTextChange(key, event.target.value)}
                  disabled={saving}
                  className={inputClass}
                />
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-4 flex gap-2">
        <button
          type="button"
          onClick={onCancel}
          className={secondaryButtonClass}
          disabled={saving}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => onSave(buildChangedBody())}
          className={primaryButtonClass}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
};

export default PartnerApplicationEditor;
